import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import type {
  AggregatedStat,
  ITrackedLink,
  ITrafficSources,
  TAttributionModel,
  TrackedLinkCampaign,
} from '@feathr/blackbox';
import { reportModuleLabels } from '@feathr/blackbox';
import type { ITanStackTableProps } from '@feathr/components';
import { StatsCardV1, TanStackTable } from '@feathr/components';

import type { ITrafficBreakdownRow, ITrafficBreakdownTotals } from './trafficBreakdownColumns';
import { getTrafficBreakdownColumns } from './trafficBreakdownColumns';

import * as styles from './TrafficBreakdownCard.css';

type TSort = NonNullable<ITanStackTableProps<ITrafficBreakdownRow>['sort']>;

const DEFAULT_PAGE_SIZE = 20;

interface IProps {
  aggregatedStats: AggregatedStat;
  campaign: TrackedLinkCampaign;
  attributionModel: TAttributionModel;
}

// Build one row per tracked_link utm_source, filling metrics from the traffic object.
function buildRows(
  campaign: TrackedLinkCampaign,
  aggregatedStats: AggregatedStat,
  attributionModel: TAttributionModel,
): ITrafficBreakdownRow[] {
  const sources = campaign
    .get('tracked_links')
    .reduce((acc: Record<string, ITrafficBreakdownTotals>, link: ITrackedLink) => {
      acc[link.utm_source] = { views: 0, sessions: 0, conversions: 0 };
      return acc;
    }, {});

  const traffic = aggregatedStats.get('traffic') as ITrafficSources;
  if (traffic) {
    (Object.keys(traffic) as Array<keyof ITrafficSources>).forEach((key) => {
      const parsedKey = key.toString().replace(/\+/, ' ');
      if (!sources[parsedKey]) {
        return;
      }
      sources[parsedKey].views = traffic[key]?.views || 0;
      sources[parsedKey].sessions = traffic[key]?.sessions || 0;
      sources[parsedKey].conversions = traffic[key]?.conversions?.[attributionModel] || 0;
    });
  }

  return Object.entries(sources).map(([source, data]) => ({ id: source, source, ...data }));
}

// Sort in memory by the active key (stable, so ties keep tracked_links order).
function sortRows(rows: ITrafficBreakdownRow[], sort: TSort): ITrafficBreakdownRow[] {
  if (sort.length === 0) {
    return rows;
  }
  const [{ id, desc = false }] = sort;
  return [...rows].sort((a, b) => {
    const cmp =
      id === 'source'
        ? a.source.localeCompare(b.source)
        : a[id as 'views' | 'sessions' | 'conversions'] -
          b[id as 'views' | 'sessions' | 'conversions'];
    return desc ? -cmp : cmp;
  });
}

function sumTotals(rows: ITrafficBreakdownRow[]): ITrafficBreakdownTotals {
  return rows.reduce(
    (totals, row) => ({
      views: totals.views + row.views,
      sessions: totals.sessions + row.sessions,
      conversions: totals.conversions + row.conversions,
    }),
    { views: 0, sessions: 0, conversions: 0 },
  );
}

function TrafficBreakdownCard({
  aggregatedStats,
  campaign,
  attributionModel,
}: Readonly<IProps>): JSX.Element {
  const { t } = useTranslation();
  const [sort, setSort] = useState<TSort>([]);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  // Client-side sort + paginate; the source set is already loaded in memory.
  // Unsorted rows follow tracked_links insertion order.
  const rows = buildRows(campaign, aggregatedStats, attributionModel);
  const totals = sumTotals(rows);
  const sortedRows = sortRows(rows, sort);

  const pageCount = Math.max(1, Math.ceil(sortedRows.length / pageSize));
  const clampedPage = Math.min(page, pageCount - 1);
  const pageRows = sortedRows.slice(clampedPage * pageSize, clampedPage * pageSize + pageSize);

  const columns = getTrafficBreakdownColumns(t, totals);

  function handleSortChange(nextSort: TSort): void {
    setSort(nextSort);
    setPage(0);
  }

  function handlePageSizeChange(nextPageSize: number): void {
    setPageSize(nextPageSize);
    setPage(0);
  }

  return (
    <StatsCardV1 title={reportModuleLabels.includeTrafficBreakdown}>
      <TanStackTable<ITrafficBreakdownRow>
        className={styles.tableWrapper}
        columns={columns}
        idKey={'id'}
        items={pageRows}
        noDataText={t('No Data')}
        onPageChange={setPage}
        onPageSizeChange={handlePageSizeChange}
        onSortChange={handleSortChange}
        page={clampedPage}
        pages={pageCount}
        pageSize={pageSize}
        sort={sort}
      />
    </StatsCardV1>
  );
}

export default observer(TrafficBreakdownCard);
